import { SpaLiveLabels } from '../../../mqttData/live/spaLiveLabels.enums';
import { SpaSettingsLabels } from '../../../mqttData/settings/spaSettingsLabels';
import { SpaConstLabels } from '../../../mqttData/consts/spaConstsLabels.enums';
import { peakV1ToScreen } from './v1PeakAdapter';

/**
 * V1 (legacy Mosquitto broker) wire payloads → the portal's V2-shaped redux slices.
 *
 * The legacy server publishes friendly field names on a handful of topics; the portal's
 * pages read the Yocto/AWS IoT keys (`STemp`, `P1`, `cfgP1`, `TSP`, `setTSPmin`, ...).
 * Each transform returns a PATCH for the `live`, `settings` and `const` slices, with
 * only the keys the payload actually carried: Coldfire packs publish partial telemetry
 * (`SpaLive.ToJson` writes only what the pack sent), so a message must never reset the
 * fields it does not mention. The hook merges patches into its running snapshot.
 *
 * Ported from the mobile app's `services/mqtt/v1/transforms.js`, which was confirmed
 * against real YOCTUB and Coldfire spas; only the output key names differ.
 */

export type V1Channel =
  | 'live'
  | 'settings'
  | 'config'
  | 'connection'
  | 'spaboyLive'
  | 'spaboySettings'
  | 'peak'
  | 'network'
  | 'info'
  | 'update'
  | 'errors';

/** Topic suffix under `arctic/spa/{guid}/` for each channel. */
export const V1_CHANNEL_TOPIC: Record<V1Channel, string> = {
  live: 'telemetry/spa',
  settings: 'settings/spa',
  config: 'config/spa',
  connection: 'telemetry/heartbeat',
  spaboyLive: 'telemetry/spaboy',
  spaboySettings: 'settings/spaboy',
  peak: 'settings/peak',
  network: 'information/network',
  // Retained on the broker: pack/spa/spaboy firmware + product family (YOCTUB vs Coldfire).
  info: 'information/spa',
  // Only while a firmware update is in flight.
  update: 'telemetry/update',
  // Active faults, retained. Decoded by v1Errors, not a slice transform.
  errors: 'telemetry/errors',
};

/** Channels whose arrival proves the spa itself is online right now. */
export const V1_ACTIVITY_CHANNELS: ReadonlySet<V1Channel> = new Set<V1Channel>(['live', 'connection', 'spaboyLive']);

export interface V1SlicePatch {
  live?: Record<string, any>;
  settings?: Record<string, any>;
  const?: Record<string, any>;
}

/** What the hook has merged so far; transforms that depend on another channel read it. */
export interface V1Snapshot {
  live?: Record<string, any>;
  settings?: Record<string, any>;
  const?: Record<string, any>;
}

/**
 * `information/spa` → `pack.product` is "YOCTUB" on Yocto-class packs still on the V1
 * stack (the legacy portal's PRODUCT_CODE.*_YOCTUB). Coldfire packs report their own
 * product names.
 */
export const isYoctoProduct = (product: unknown): boolean =>
  typeof product === 'string' && /YOCTUB/i.test(product);

const isObject = (p: unknown): p is Record<string, any> => !!p && typeof p === 'object';

/**
 * V1 pumps and blowers are 0/1/2 speeds (off / low / high). The portal renders pumps on
 * the Yocto motor scale, where `pumpLevel` reads > 15 as high and > 0 as low.
 */
export const speedToUi = (raw: unknown): number => {
  const v = Number(raw) || 0;
  if (v >= 2) return 100;
  if (v >= 1) return 15;
  return 0;
};

/** Inverse of speedToUi: portal 0/15/100 → V1 wire 0/1/2. */
export const uiToSpeed = (ui: unknown): number => {
  const v = Number(ui) || 0;
  if (v > 15) return 2;
  if (v > 0) return 1;
  return 0;
};

const onOff = (v: unknown): boolean => (typeof v === 'number' ? v > 0 : !!v);

/** `telemetry/spa` (SpaLive). */
export function transformLive(payload: unknown, ctx?: V1Snapshot): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const live: Record<string, any> = {};
  const settings: Record<string, any> = {};

  if (payload.tempF != null) live[SpaLiveLabels.CurrentTemp] = Number(payload.tempF);
  // The V1 setpoint lives here (tempSetPointF); settings/spa often has it as null. The
  // pages read it as `TSP` (the Yocto reading key), the same way transformSettings emits it.
  if (payload.tempSetPointF != null) settings.TSP = Number(payload.tempSetPointF);

  const pumps: [string, string][] = [
    ['pump1', SpaLiveLabels.Pump1],
    ['pump2', SpaLiveLabels.Pump2],
    ['pump3', SpaLiveLabels.Pump3],
    ['pump4', SpaLiveLabels.Pump4],
    ['pump5', SpaLiveLabels.Pump5],
    ['blower1', SpaLiveLabels.BL1],
    ['blower2', SpaLiveLabels.BL2],
  ];
  pumps.forEach(([wire, label]) => {
    if (payload[wire] !== undefined) live[label] = speedToUi(payload[wire]);
  });

  const toggles: [string, string][] = [
    ['lights', SpaLiveLabels.Lights],
    ['fogger', SpaLiveLabels.Fogger],
    ['sds', SpaLiveLabels.SDS],
    ['yess', SpaLiveLabels.Yess],
    ['allOn', SpaLiveLabels.AllOn],
    ['onzen', SpaLiveLabels.On],
    ['ozone', SpaLiveLabels.Oz],
    ['economy', SpaLiveLabels.Econ],
  ];
  toggles.forEach(([wire, label]) => {
    if (payload[wire] !== undefined) live[label] = onOff(payload[wire]);
  });

  // The filter pump's state is the same 0..7 integer the Yocto stack reports (idle, purging,
  // filtering, suspended, ...); the Filtration page's state table reads it and only lets a
  // manual cycle start from idle or suspended, so it must not be flattened to on/off.
  if (payload.filter !== undefined) live[SpaLiveLabels.Filter] = Number(payload.filter) || 0;

  // Heater states drive the Idle / Warm-Up / Heating chip, same ints as V2's H1/H2.
  if (payload.heater1 !== undefined) live[SpaLiveLabels.H1] = Number(payload.heater1) || 0;
  if (payload.heater2 !== undefined) live[SpaLiveLabels.H2] = Number(payload.heater2) || 0;

  // Pack current. The legacy portal (UC_ColdFire_AmpMeter) showed amps as
  // CurrentADC / 100 on Yocto-class packs and CurrentADC / 1000 on Coldfire packs. The
  // portal's gauge divides `Current` by 100, so Yocto passes through and Coldfire is /10.
  // The pack type comes from information/spa (retained, arrives before telemetry).
  const currentDivisor = isYoctoProduct(ctx?.settings?.v1Product) ? 1 : 10;
  if (payload.currentADC != null) {
    live[SpaLiveLabels.Current] = Math.round((Number(payload.currentADC) || 0) / currentDivisor);
  } else if (payload.current != null && payload.ph === undefined) {
    live[SpaLiveLabels.Current] = Math.round((Number(payload.current) || 0) / currentDivisor);
  }

  const out: V1SlicePatch = {};
  if (Object.keys(live).length) out.live = live;
  if (Object.keys(settings).length) out.settings = settings;
  return out;
}

interface RangeValue { current?: number | null; min?: number | null; max?: number | null; }

const range = (r: unknown): RangeValue | undefined => (isObject(r) ? { current: r.current, min: r.min, max: r.max } : undefined);

/**
 * `settings/spa` (SpaSettings): filtration / onzen / ozone as {min,max,current} ranges
 * plus flat fields. The range bounds feed the `const` slice the steppers clamp against.
 */
export function transformSettings(payload: unknown): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const settings: Record<string, any> = {};
  const consts: Record<string, any> = {};

  const apply = (
    wire: string,
    key: string | null,
    minKey: string,
    maxKey: string,
    transform: (v: any) => any = (v) => v,
  ) => {
    const r = range(payload[wire]);
    if (!r) return;
    if (key && r.current !== undefined && r.current !== null) settings[key] = transform(r.current);
    if (r.min !== undefined && r.min !== null) consts[minKey] = r.min;
    if (r.max !== undefined && r.max !== null) consts[maxKey] = r.max;
  };

  // settings/spa sends temperature.current as null on some spas; the live setpoint
  // comes from telemetry/spa (tempSetPointF) and must not be clobbered here.
  apply('temperature', SpaSettingsLabels.setTSP, SpaConstLabels.setTSPmin, SpaConstLabels.setTSPmax);
  apply('filterFrequency', 'FF', SpaConstLabels.setFFmin, SpaConstLabels.setFFmax);
  apply('filterDuration', 'FD', SpaConstLabels.setFDmin, SpaConstLabels.setFDmax);
  apply('onzenHours', 'OnHr', SpaConstLabels.setOnHrmin, SpaConstLabels.setOnHrmax);
  apply('onzenCycles', 'OnCy', SpaConstLabels.setOnCymin, SpaConstLabels.setOnCymax);
  apply('ozoneHours', 'OzHr', SpaConstLabels.setOzHrmin, SpaConstLabels.setOzHrmax);
  apply('ozoneCycles', 'OzCy', SpaConstLabels.setOzCymin, SpaConstLabels.setOzCymax);

  // The pages read the setpoint as `TSP` (the Yocto reading key), not `setTSP`.
  if (settings[SpaSettingsLabels.setTSP] !== undefined) {
    settings.TSP = settings[SpaSettingsLabels.setTSP];
    delete settings[SpaSettingsLabels.setTSP];
  }

  if (payload.filterSuspension !== undefined) settings[SpaSettingsLabels.FS] = !!payload.filterSuspension;
  if (payload.tempOffset !== undefined) settings[SpaSettingsLabels.cfgToff] = payload.tempOffset;
  if (payload.spaboyHours !== undefined) settings.SBHr = payload.spaboyHours;

  const out: V1SlicePatch = {};
  if (Object.keys(settings).length) out.settings = settings;
  if (Object.keys(consts).length) out.const = consts;
  return out;
}

/**
 * `config/spa` (SpaConfig): which pumps and accessories the spa has. The pages gate
 * every control on these `cfg*` flags, so this is what makes V1 controls appear.
 */
export function transformConfig(payload: unknown): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const settings: Record<string, any> = {
    [SpaSettingsLabels.cfgP1]: !!payload.pump1,
    [SpaSettingsLabels.cfgP2]: !!payload.pump2,
    [SpaSettingsLabels.cfgP3]: !!payload.pump3,
    [SpaSettingsLabels.cfgP4]: !!payload.pump4,
    [SpaSettingsLabels.cfgP5]: !!payload.pump5,
    [SpaSettingsLabels.cfgB1]: !!payload.blower1,
    [SpaSettingsLabels.cfgB2]: !!payload.blower2,
    [SpaSettingsLabels.cfgSDS]: !!payload.sds,
    [SpaSettingsLabels.cfgYESS]: !!payload.yess,
    [SpaSettingsLabels.cfgFG]: !!payload.fogger,
    [SpaSettingsLabels.cfgLi]: !!payload.lights,
    // SpaConfig.spaboy is an integer (0/1); onzen / ozonePeak1 / ozonePeak2 are booleans.
    [SpaSettingsLabels.cfgSB]: (Number(payload.spaboy) || 0) > 0,
    [SpaSettingsLabels.cfgOn]: !!payload.onzen,
    [SpaSettingsLabels.cfgOzP1]: !!payload.ozonePeak1,
    [SpaSettingsLabels.cfgOzP2]: !!payload.ozonePeak2,
  };
  return { settings };
}

const colorIndex = (c: unknown): number | undefined => {
  if (c == null) return undefined;
  const n = Number(c);
  return Number.isFinite(n) ? n : undefined;
};

const SPABOY_PRODUCING_STATE = 3;
const SPABOY_PRODUCING_CURRENT_THRESHOLD = 50;

/** `telemetry/spaboy` (SpaboyLive) → the `sb*` live keys the SpaBoy page reads. */
export function transformSpaboyLive(payload: unknown, ctx?: V1Snapshot): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const current = Number(payload.current) || 0;
  const eState = Number(payload.eState) || 0;
  // V1 has no `producing` flag — derive it as the legacy apps do (SpaboyLive.isProducingLegacy).
  // Yocto-class packs leave eState at 0 even with the cell running (captured on a production
  // spa: current 1008, voltage 13721, eState 0), so for them the current alone decides.
  const stateAgrees = isYoctoProduct(ctx?.settings?.v1Product) || eState === SPABOY_PRODUCING_STATE;
  const producing = payload.producing != null
    ? !!payload.producing
    : current > SPABOY_PRODUCING_CURRENT_THRESHOLD && stateAgrees;

  // V1 SpaboyLive has no `boost` flag either: a running boost shows up as the cell producing,
  // so the SpaBoy page's "Boosting…" state follows `producing` (as the mobile app does).
  const live: Record<string, any> = {
    [SpaLiveLabels.sbProducing]: producing,
    [SpaLiveLabels.sbBoost]: payload.boost != null ? !!payload.boost : producing,
  };
  if (payload.ph != null) live[SpaLiveLabels.sbpH] = Number(payload.ph);
  if (payload.orp != null) live[SpaLiveLabels.sbORP] = Number(payload.orp);
  if (payload.eWear != null) live[SpaLiveLabels.sbWear] = Number(payload.eWear);
  const phInd = colorIndex(payload.phColor);
  const orpInd = colorIndex(payload.orpColor);
  if (phInd !== undefined) live[SpaLiveLabels.sbpHind] = phInd;
  if (orpInd !== undefined) live[SpaLiveLabels.sbORPind] = orpInd;
  // Reaching this channel at all means the spa has a Spa Boy, even when config/spa says 0.
  return { live, settings: { [SpaSettingsLabels.cfgSB]: true } };
}

/** `settings/spaboy` (SpaboySettings): ORP / pH ranges and hours. */
export function transformSpaboySettings(payload: unknown): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const settings: Record<string, any> = {};
  const orp = range(payload.orp);
  const ph = range(payload.ph);
  if (orp) {
    if (orp.min != null) settings[SpaSettingsLabels.SBORPlo] = orp.min;
    if (orp.max != null) settings[SpaSettingsLabels.SBORPhi] = orp.max;
  }
  if (ph) {
    if (ph.min != null) settings[SpaSettingsLabels.SBpHlo] = ph.min;
    if (ph.max != null) settings[SpaSettingsLabels.SBpHhi] = ph.max;
  }
  if (payload.spaboyHours !== undefined) settings.SBHr = payload.spaboyHours;
  if (payload.tempOffset !== undefined) settings[SpaSettingsLabels.cfgToff] = payload.tempOffset;
  return Object.keys(settings).length ? { settings } : null;
}

/** `settings/peak` (PeakSettings) → the Economy Mode page's Mode/EndMode fields. */
export function transformPeak(payload: unknown): V1SlicePatch | null {
  const screen = peakV1ToScreen(payload);
  return screen ? { settings: screen } : null;
}

/** `information/network` (NetworkInfo) → the network fields the V2 sett topic carries. */
export function transformNetwork(payload: unknown): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const settings: Record<string, any> = {};
  if (payload.signal_strength != null) settings[SpaSettingsLabels.Signal_Strength] = payload.signal_strength;
  if (payload.connection_type != null) settings[SpaSettingsLabels.Connection_Type] = String(payload.connection_type).toLowerCase();
  if (payload.public_ip != null) settings[SpaSettingsLabels.Public_IP] = payload.public_ip;
  if (payload.private_ip != null) settings[SpaSettingsLabels.Private_IP] = payload.private_ip;
  return Object.keys(settings).length ? { settings } : null;
}

/**
 * `information/spa` (SpaInformation): firmware versions and product family. Surfaced
 * under the same keys the V2 sett topic uses so the details widgets can read them.
 */
export function transformInfo(payload: unknown): V1SlicePatch | null {
  if (!isObject(payload)) return null;
  const settings: Record<string, any> = {};
  if (payload.pack?.firmware != null) settings[SpaSettingsLabels.LPCFWVer] = payload.pack.firmware;
  if (payload.spa?.software != null) settings[SpaSettingsLabels.YOCFWVer] = payload.spa.software;
  if (payload.spaboy?.firmware != null) settings[SpaSettingsLabels.SBFWVer] = payload.spaboy.firmware;
  if (payload.pack?.product != null) settings.v1Product = payload.pack.product;
  return Object.keys(settings).length ? { settings } : null;
}

/** Transform for each channel; channels without one carry no slice data. */
export type V1Transform = (payload: unknown, ctx?: V1Snapshot) => V1SlicePatch | null;

export const V1_CHANNEL_TRANSFORM: Partial<Record<V1Channel, V1Transform>> = {
  live: transformLive,
  settings: transformSettings,
  config: transformConfig,
  spaboyLive: transformSpaboyLive,
  spaboySettings: transformSpaboySettings,
  peak: transformPeak,
  network: transformNetwork,
  info: transformInfo,
};
